import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { evaluationStudio, scenarios } from '../../../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { showConfirm, showWarning, showInfo } from '../../../utils/alerts';
import { AdminPageHeader } from '../components/AdminPageHeader';

interface CompetencyItem {
  id: string;
  name: string;
  weight: number;
  description: string;
}

interface CriterionItem {
  id: string;
  name: string;
  competencyId?: string;
  weight: number;
  criticalFlag: boolean;
  description: string;
  passingAnchor?: string;
  failingAnchor?: string;
}

interface EvaluationConfigData {
  id?: string;
  simulationId: string;
  name: string;
  description: string;
  status: 'DRAFT' | 'TESTED' | 'APPROVED' | 'PUBLISHED' | 'ARCHIVED';
  version: number;
  evaluationPrompt: string;
  coachingPrompt: string;
  resultPrompt: string;
  scoringScale: {
    min: number;
    max: number;
    passMark: number;
    criticalFailureAction?: string;
  };
  competencies: CompetencyItem[];
  criteria: CriterionItem[];
  evidenceSources: {
    transcript: boolean;
    communicationMetrics: boolean;
    videoEvidence: boolean;
    nlpEvidence: boolean;
  };
  outputSchema?: any;
  coachingSchema?: any;
  resultSchema?: any;
  llmProvider: string;
  llmModel: string;
  temperature: number;
  maxTokens: number;
  updatedAt?: string;
}

interface CaseItem {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  totalConfigs: number;
  publishedVersion: number | null;
  hasDraft: boolean;
  evaluationStatus: string;
  updatedAt: string;
}

export const EvaluationStudioPage: React.FC = () => {
  const { scenarioId: urlScenarioId } = useParams<{ scenarioId?: string }>();
  const navigate = useNavigate();
  const { isTenantAdmin, isPlatformAdmin } = usePermissions();

  // Selected Case State
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>(urlScenarioId || '');
  const [allVersions, setAllVersions] = useState<EvaluationConfigData[]>([]);

  // Studio Active Tab
  const [activeTab, setActiveTab] = useState<
    'general' | 'competencies' | 'criteria' | 'scoring' | 'evidence' | 'prompts' | 'llm' | 'preview' | 'test' | 'golden' | 'diff' | 'audit'
  >('general');

  // Main Editor Configuration State
  const [config, setConfig] = useState<EvaluationConfigData>({
    simulationId: urlScenarioId || '',
    name: 'Standard Evaluation Configuration',
    description: 'Evidence-grounded evaluation and coaching rubric.',
    status: 'DRAFT',
    version: 1,
    evaluationPrompt:
      'You are an expert simulation evaluator.\nEvaluate the learner transcript {{transcript}} against {{competencies}} and {{criteria}} using {{scoringScale}}.\nVideo observations: {{videoEvidence}}.\nCommunication metrics: {{communicationMetrics}}.\nReturn strict structured JSON output.',
    coachingPrompt:
      'You are a professional behavioral coach.\nBased on the evaluation {{evaluation}} and evidence observations {{videoEvidence}}, generate actionable coaching points with practice activities.\nReturn structured JSON.',
    resultPrompt:
      'Generate executive performance summary for the learner using {{evaluation}} and {{coaching}}.\nReturn structured JSON.',
    scoringScale: { min: 1, max: 5, passMark: 70, criticalFailureAction: 'CAP_AT_FAILING' },
    competencies: [
      { id: 'discovery', name: 'Discovery & Questioning', weight: 0.35, description: 'Understands client requirements thoroughly.' },
      { id: 'objection_handling', name: 'Objection Handling', weight: 0.35, description: 'Handles pushback constructively with evidence.' },
      { id: 'communication', name: 'Communication & Professionalism', weight: 0.30, description: 'Clear, concise, and structured dialogue.' },
    ],
    criteria: [
      { id: 'needs_discovery', name: 'Comprehensive Needs Discovery', competencyId: 'discovery', weight: 0.35, criticalFlag: false, description: 'Asks open questions to identify core pain points.', passingAnchor: 'Asks 3+ probing questions before proposing solution', failingAnchor: 'Jumps directly to solution without asking needs' },
      { id: 'objection_empathy', name: 'Objection Empathy & Ownership', competencyId: 'objection_handling', weight: 0.35, criticalFlag: true, description: 'Acknowledges customer concern and takes accountability.', passingAnchor: 'Directly acknowledges frustration and states clear action plan', failingAnchor: 'Dismisses concern or blames policies' },
      { id: 'professional_dialogue', name: 'Professional Structure', competencyId: 'communication', weight: 0.30, criticalFlag: false, description: 'Clear, respectful, and articulate communication.', passingAnchor: 'Concise, structured with clear closing statement', failingAnchor: 'Unstructured rambling or inappropriate tone' },
    ],
    evidenceSources: {
      transcript: true,
      communicationMetrics: true,
      videoEvidence: true,
      nlpEvidence: false,
    },
    llmProvider: 'openai',
    llmModel: 'gpt-4o',
    temperature: 0.2,
    maxTokens: 1500,
  });

  // UI / Async States
  const [initialConfig, setInitialConfig] = useState<EvaluationConfigData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [concurrencyConflict, setConcurrencyConflict] = useState<boolean>(false);

  // Preview & Testing States
  const [promptPreview, setPromptPreview] = useState<{ rendered: string; variablesUsed: string[]; estimatedTokens: number; estimatedCost: number } | null>(null);
  const [previewingPromptType, setPreviewingPromptType] = useState<'evaluation' | 'coaching' | 'result'>('evaluation');
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState<boolean>(false);

  // Golden Calibration & Diff States
  const [goldenResult, setGoldenResult] = useState<any>(null);
  const [isGoldenRunning, setIsGoldenRunning] = useState<boolean>(false);
  const [diffResult, setDiffResult] = useState<any>(null);
  const [selectedDiffVersionId, setSelectedDiffVersionId] = useState<string>('');
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  // Track Unsaved Changes
  const hasUnsavedChanges = useMemo(() => {
    if (!initialConfig) return false;
    return JSON.stringify(config) !== JSON.stringify(initialConfig);
  }, [config, initialConfig]);

  // Load Cases List
  useEffect(() => {
    setIsLoading(true);
    evaluationStudio
      .listCases()
      .then((res: any) => {
        const caseList = res.data || [];
        setCases(caseList);
        if (urlScenarioId) {
          setSelectedCaseId(urlScenarioId);
        } else if (!selectedCaseId && caseList.length > 0) {
          setSelectedCaseId(caseList[0].id);
        }
      })
      .catch((err) => {
        setStatusMessage({ type: 'error', text: `Failed to load cases: ${err.message}` });
      })
      .finally(() => setIsLoading(false));
  }, [urlScenarioId]);

  // Load Configurations for Selected Case
  useEffect(() => {
    if (!selectedCaseId) return;

    setIsLoading(true);
    setStatusMessage(null);
    setValidationErrors([]);
    setConcurrencyConflict(false);

    Promise.all([
      evaluationStudio.listConfigs(selectedCaseId).catch(() => ({ data: [] })),
      evaluationStudio.getPublishedConfig(selectedCaseId).catch(() => ({ data: null })),
      evaluationStudio.getAuditTrail(selectedCaseId).catch(() => ({ data: [] })),
      scenarios.get(selectedCaseId).catch(() => ({ data: null })),
    ])
      .then(([configsRes, publishedRes, auditRes, scenarioRes]: [any, any, any, any]) => {
        const versions = configsRes.data || [];
        setAllVersions(versions);
        setAuditLogs(auditRes.data || []);

        const published = publishedRes.data;
        const draft = versions.find((v: any) => v.status === 'DRAFT' || v.status === 'TESTED' || v.status === 'APPROVED');
        const activeToLoad = draft || published || versions[0];
        const scenarioData = scenarioRes?.data || null;

        if (activeToLoad) {
          const loaded: EvaluationConfigData = {
            ...activeToLoad,
            scoringScale: activeToLoad.scoringScale || { min: 1, max: 5, passMark: scenarioData?.passingScore || 70, criticalFailureAction: 'CAP_AT_FAILING' },
            competencies: activeToLoad.competencies || [],
            criteria: activeToLoad.criteria || [],
            evidenceSources: activeToLoad.evidenceSources || { transcript: true, communicationMetrics: true, videoEvidence: true, nlpEvidence: false },
          };
          setConfig(loaded);
          setInitialConfig(loaded);
        } else {
          // Initialize fresh draft template dynamically from scenario's own rubric items
          const scenarioTitle = scenarioData?.title || cases.find((c) => c.id === selectedCaseId)?.title || 'Case';
          const scenarioRubrics = Array.isArray(scenarioData?.rubricItems) && scenarioData.rubricItems.length > 0
            ? scenarioData.rubricItems
            : [];

          const dynamicCompetencies: CompetencyItem[] = scenarioRubrics.length > 0
            ? scenarioRubrics.map((r: any, idx: number) => ({
                id: `comp_${idx + 1}`,
                name: r.title || r.name || `Competency ${idx + 1}`,
                weight: r.weight ? Number(r.weight) / 100 : Number((1 / scenarioRubrics.length).toFixed(2)),
                description: r.description || `Assessment of ${r.title || 'required competency'}.`,
              }))
            : [
                { id: 'discovery', name: 'Discovery & Questioning', weight: 0.35, description: 'Understands client requirements thoroughly.' },
                { id: 'objection_handling', name: 'Objection Handling', weight: 0.35, description: 'Handles pushback constructively with evidence.' },
                { id: 'communication', name: 'Communication & Professionalism', weight: 0.30, description: 'Clear, concise, and structured dialogue.' },
              ];

          const dynamicCriteria: CriterionItem[] = scenarioRubrics.length > 0
            ? scenarioRubrics.map((r: any, idx: number) => ({
                id: `crit_${idx + 1}`,
                name: r.title || r.name || `Criterion ${idx + 1}`,
                competencyId: `comp_${idx + 1}`,
                weight: r.weight ? Number(r.weight) / 100 : Number((1 / scenarioRubrics.length).toFixed(2)),
                criticalFlag: Boolean(r.critical || r.criticalFlag || r.isCritical),
                description: r.description || `Evaluates performance for ${r.title || 'criterion'}.`,
                passingAnchor: r.passingAnchor || `Demonstrates ${r.title || 'required skills'} clearly and appropriately.`,
                failingAnchor: r.failingAnchor || `Fails to demonstrate ${r.title || 'required skills'} or misses key requirements.`,
              }))
            : [
                { id: 'needs_discovery', name: 'Comprehensive Needs Discovery', competencyId: 'discovery', weight: 0.35, criticalFlag: false, description: 'Asks open questions to identify core pain points.' },
                { id: 'objection_empathy', name: 'Objection Empathy & Ownership', competencyId: 'objection_handling', weight: 0.35, criticalFlag: true, description: 'Acknowledges customer concern and takes accountability.' },
                { id: 'professional_dialogue', name: 'Professional Structure', competencyId: 'communication', weight: 0.30, criticalFlag: false, description: 'Clear, respectful, and articulate communication.' },
              ];

          const fresh: EvaluationConfigData = {
            simulationId: selectedCaseId,
            name: `${scenarioTitle} Evaluation Config`,
            description: scenarioData?.description || 'Configurable case evaluation and coaching rubric.',
            status: 'DRAFT',
            version: 1,
            evaluationPrompt:
              'You are an expert simulation evaluator.\nEvaluate the learner transcript {{transcript}} against {{competencies}} and {{criteria}} using {{scoringScale}}.\nVideo observations: {{videoEvidence}}.\nCommunication metrics: {{communicationMetrics}}.\nReturn strict structured JSON output.',
            coachingPrompt:
              'You are a professional coaching engine.\nBased on the evaluation {{evaluation}} and evidence observations {{videoEvidence}}, generate actionable coaching points with practice activities.\nReturn structured JSON.',
            resultPrompt:
              'Generate executive performance summary for the learner using {{evaluation}} and {{coaching}}.\nReturn structured JSON.',
            scoringScale: { min: 1, max: 5, passMark: scenarioData?.passingScore || 70, criticalFailureAction: 'CAP_AT_FAILING' },
            competencies: dynamicCompetencies,
            criteria: dynamicCriteria,
            evidenceSources: { transcript: true, communicationMetrics: true, videoEvidence: true, nlpEvidence: false },
            llmProvider: 'openai',
            llmModel: 'gpt-4o',
            temperature: 0.2,
            maxTokens: 1500,
          };
          setConfig(fresh);
          setInitialConfig(fresh);
        }
      })
      .finally(() => setIsLoading(false));
  }, [selectedCaseId]);

  // Handle Case Switching
  const handleCaseChange = async (caseId: string) => {
    if (hasUnsavedChanges) {
      const confirmed = await showConfirm({
        title: 'Discard Unsaved Changes?',
        text: 'You have unsaved changes in the current evaluation configuration. Discard and switch case?',
        confirmText: 'Discard & Switch',
        cancelText: 'Stay Here',
        icon: 'warning',
      });
      if (!confirmed) {
        return;
      }
    }
    setSelectedCaseId(caseId);
    navigate(`/scenarios/${caseId}/evaluation-studio`, { replace: true });
  };

  // Allowed Trainova Evaluation Template Variables (Phase 14 & Phase 27)
  const ALLOWED_PROMPT_VARS = [
    'scenario', 'scenarioName', 'scenarioDescription', 'learner', 'attempt',
    'transcript', 'conversation', 'communicationMetrics', 'videoEvidence',
    'videoObservations', 'nlpEvidence', 'evidence', 'competencies', 'criteria',
    'rubric', 'evaluation', 'coaching', 'scores', 'previousAttempts',
    'resultSchema', 'coachingSchema', 'scoringScale', 'resultSections'
  ];

  // Helper to intelligently sanitize and auto-fix prompt templates
  const autoFixPromptTemplate = (text: string, type: 'evaluation' | 'coaching' | 'result'): string => {
    let fixed = text || '';

    // 1. Fix malformed single curly brackets {var} or triple {{{var}}} -> {{var}}
    fixed = fixed.replace(/\{\{\{\s*([a-zA-Z0-9_]+)\s*\}\}\}/g, '{{$1}}');
    fixed = fixed.replace(/(?<!\{)\{([a-zA-Z0-9_]+)\}(?!\})/g, '{{$1}}');

    // 2. Fix variable aliases and typos
    const aliasMap: Record<string, string> = {
      dialogue: 'transcript',
      turns: 'transcript',
      convo: 'transcript',
      messages: 'transcript',
      conversation_history: 'transcript',
      case: 'scenario',
      simulation: 'scenario',
      scenario_title: 'scenarioName',
      scenario_desc: 'scenarioDescription',
      metrics: 'communicationMetrics',
      comm_metrics: 'communicationMetrics',
      speech_metrics: 'communicationMetrics',
      video: 'videoObservations',
      video_observations: 'videoObservations',
      video_evidence: 'videoEvidence',
      camera_observations: 'videoObservations',
      nlp: 'nlpEvidence',
      skills: 'competencies',
      competency_list: 'competencies',
      rubrics: 'rubric',
      scoring_rubric: 'rubric',
      scale: 'scoringScale',
      score_scale: 'scoringScale',
      feedback: 'coaching',
      coaching_points: 'coaching',
      schema: type === 'coaching' ? 'coachingSchema' : 'resultSchema',
      json_schema: type === 'coaching' ? 'coachingSchema' : 'resultSchema',
      results: 'resultSections',
    };

    fixed = fixed.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, varName) => {
      const lower = varName.toLowerCase();
      if (ALLOWED_PROMPT_VARS.includes(varName)) {
        return `{{${varName}}}`;
      }
      if (aliasMap[lower]) {
        return `{{${aliasMap[lower]}}}`;
      }
      return match;
    });

    return fixed;
  };

  const handleAutoFixPrompts = () => {
    const fixedEvaluationPrompt = autoFixPromptTemplate(config.evaluationPrompt, 'evaluation');
    const fixedCoachingPrompt = autoFixPromptTemplate(config.coachingPrompt, 'coaching');
    const fixedResultPrompt = autoFixPromptTemplate(config.resultPrompt, 'result');

    setConfig((prev) => ({
      ...prev,
      evaluationPrompt: fixedEvaluationPrompt,
      coachingPrompt: fixedCoachingPrompt,
      resultPrompt: fixedResultPrompt,
    }));

    setValidationErrors((prev) =>
      prev.filter((err) => !err.toLowerCase().includes('variable') && !err.toLowerCase().includes('prompt') && !err.toLowerCase().includes('template'))
    );
    setStatusMessage({
      type: 'success',
      text: '⚡ Prompts and template variables auto-fixed successfully!',
    });
  };

  const handleResetToGoldenPrompts = () => {
    const defaultEval = 'You are an expert simulation evaluator.\nEvaluate the learner transcript {{transcript}} against {{competencies}} and {{criteria}} using {{scoringScale}}.\nVideo observations: {{videoEvidence}}.\nCommunication metrics: {{communicationMetrics}}.\nReturn strict structured JSON output.';
    const defaultCoach = 'You are a professional behavioral coach.\nBased on the evaluation {{evaluation}} and evidence observations {{videoEvidence}}, generate actionable coaching points with practice activities.\nReturn structured JSON.';
    const defaultResult = 'Generate executive performance summary for the learner using {{evaluation}} and {{coaching}}.\nReturn structured JSON.';

    setConfig((prev) => ({
      ...prev,
      evaluationPrompt: defaultEval,
      coachingPrompt: defaultCoach,
      resultPrompt: defaultResult,
    }));

    setValidationErrors((prev) =>
      prev.filter((err) => !err.toLowerCase().includes('variable') && !err.toLowerCase().includes('prompt') && !err.toLowerCase().includes('template'))
    );
    setStatusMessage({
      type: 'success',
      text: '✨ Restored standard Golden Blueprint prompts.',
    });
  };

  // Save Draft (or Create new Version)
  const handleSaveDraft = async () => {
    setIsLoading(true);
    setStatusMessage(null);
    setValidationErrors([]);
    setConcurrencyConflict(false);

    try {
      if (config.id && config.status !== 'PUBLISHED') {
        // Update existing draft with optimistic lock protection
        const res: any = await evaluationStudio.updateDraft(config.id, {
          ...config,
          expectedVersion: initialConfig?.version,
        });
        if (res.success) {
          setConfig(res.data);
          setInitialConfig(res.data);
          setStatusMessage({ type: 'success', text: 'Draft configuration saved successfully.' });
        } else {
          setValidationErrors(res.error?.details || (res.error?.message ? [res.error.message] : []));
          setStatusMessage({ type: 'error', text: res.error?.message || 'Failed to save draft' });
        }
      } else {
        // Create new draft version
        const res: any = await evaluationStudio.createDraft({
          ...config,
          simulationId: selectedCaseId,
        });
        if (res.success) {
          setConfig(res.data);
          setInitialConfig(res.data);
          setStatusMessage({ type: 'success', text: `Created new draft version v${res.data.version}.` });
          // Refresh version list
          const verRes: any = await evaluationStudio.listConfigs(selectedCaseId);
          setAllVersions(verRes.data || []);
        } else {
          setValidationErrors(res.error?.details || [res.error?.message]);
          setStatusMessage({ type: 'error', text: 'Configuration validation failed.' });
        }
      }
    } catch (err: any) {
      if (err.statusCode === 409 || err.message?.includes('CONCURRENT_MODIFICATION')) {
        setConcurrencyConflict(true);
        setStatusMessage({ type: 'error', text: 'Concurrent edit detected: another admin modified this version. Please reload.' });
      } else {
        const errorDetails = err.details || (err.message ? [err.message] : []);
        if (Array.isArray(errorDetails) && errorDetails.length > 0) {
          setValidationErrors(errorDetails);
        }
        setStatusMessage({ type: 'error', text: err.message || 'Error saving configuration' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Run Prompt Preview
  const handlePreviewPrompt = async (promptType: 'evaluation' | 'coaching' | 'result') => {
    setPreviewingPromptType(promptType);
    const template =
      promptType === 'evaluation'
        ? config.evaluationPrompt
        : promptType === 'coaching'
        ? config.coachingPrompt
        : config.resultPrompt;

    setIsLoading(true);
    try {
      const res: any = await evaluationStudio.previewPrompt({
        template,
        promptType,
      });
      if (res.success) {
        setPromptPreview(res.data);
        setActiveTab('preview');
      } else {
        setStatusMessage({ type: 'error', text: res.error?.message || 'Template preview error' });
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Run Test Evaluation
  const handleRunTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const res: any = await evaluationStudio.testEvaluation({
        config,
        promptType: 'evaluation',
      });
      setTestResult(res.data || res);
      setActiveTab('test');
    } catch (err: any) {
      setTestResult({ error: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  // Run Golden Set Calibration
  const handleRunGoldenSet = async () => {
    if (!config.id) {
      await showWarning('Draft Not Saved', 'Please save the draft configuration before running Golden Set calibration.');
      return;
    }
    setIsGoldenRunning(true);
    setGoldenResult(null);
    try {
      const res: any = await evaluationStudio.runGoldenSet(config.id);
      setGoldenResult(res.data || res);
      if (res.data?.success || res.success) {
        // Updated to TESTED
        setConfig((prev) => ({ ...prev, status: 'TESTED' }));
        setStatusMessage({ type: 'success', text: 'Golden set calibration passed! Configuration marked as TESTED.' });
      }
    } catch (err: any) {
      setGoldenResult({ error: err.message });
    } finally {
      setIsGoldenRunning(false);
    }
  };

  // Approve Configuration
  const handleApprove = async () => {
    if (!config.id) return;
    setIsLoading(true);
    try {
      const res: any = await evaluationStudio.approveConfig(config.id);
      if (res.success) {
        setConfig(res.data);
        setInitialConfig(res.data);
        setStatusMessage({ type: 'success', text: 'Configuration approved for production publishing.' });
      } else {
        setStatusMessage({ type: 'error', text: res.error?.message || 'Approval failed' });
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Publish Configuration
  const handlePublish = async () => {
    if (!config.id) {
      await handleSaveDraft();
    }
    if (!config.id) return;

    if (config.status !== 'APPROVED' && config.status !== 'TESTED') {
      const confirmed = await showConfirm({
        title: 'Uncalibrated Configuration',
        text: 'This configuration has not been explicitly calibrated or approved. Publish directly to production?',
        confirmText: 'Publish Directly',
        cancelText: 'Cancel',
        icon: 'warning',
      });
      if (!confirmed) {
        return;
      }
    }

    setIsLoading(true);
    setStatusMessage(null);
    try {
      const res: any = await evaluationStudio.publishConfig(config.id);
      if (res.success) {
        setConfig(res.data);
        setInitialConfig(res.data);
        setStatusMessage({ type: 'success', text: `v${res.data.version} is now the active published evaluation configuration in production.` });
        // Refresh version list
        const verRes: any = await evaluationStudio.listConfigs(selectedCaseId);
        setAllVersions(verRes.data || []);
      } else {
        setValidationErrors(res.error?.details || [res.error?.message]);
        setStatusMessage({ type: 'error', text: 'Cannot publish: validation errors detected.' });
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: `Publish error: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  // Rollback to Selected Version
  const handleRollback = async (targetVersionId: string) => {
    const confirmed = await showConfirm({
      title: 'Confirm Rollback',
      text: 'Rollback production evaluation to this historical version?',
      confirmText: 'Confirm Rollback',
      cancelText: 'Cancel',
      icon: 'warning',
      isDestructive: true,
    });
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const res: any = await evaluationStudio.rollbackConfig(targetVersionId);
      if (res.success) {
        setConfig(res.data);
        setInitialConfig(res.data);
        setStatusMessage({ type: 'success', text: `Successfully rolled back active production configuration to v${res.data.version}.` });
        // Refresh version list
        const verRes: any = await evaluationStudio.listConfigs(selectedCaseId);
        setAllVersions(verRes.data || []);
      } else {
        setStatusMessage({ type: 'error', text: res.error?.message || 'Rollback failed' });
      }
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // Compare Diff
  const handleRunDiff = async () => {
    if (!config.id || !selectedDiffVersionId) {
      await showInfo('Select Version', 'Select a version to compare with current configuration.');
      return;
    }
    setIsLoading(true);
    try {
      const res: any = await evaluationStudio.compareDiff({
        configIdA: selectedDiffVersionId,
        configIdB: config.id,
      });
      setDiffResult(res.data || res);
      setActiveTab('diff');
    } catch (err: any) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const totalCompetencyWeight = config.competencies.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
  const totalCriteriaWeight = config.criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);

  return (
    <div className="admin-page" style={{ paddingBottom: '80px' }}>
      {/* Top Header */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Scenarios', to: '/scenarios' },
          { label: 'AI Evaluation Studio' },
        ]}
        icon="⚖️"
        title="AI Evaluation Studio"
        badge={`v${config.version} • ${config.status}`}
        description="Configure, validate, calibrate, and publish case evaluation prompts, competency rubrics, and multi-modal scoring engines."
        actions={
          <div className="eval-studio-header-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {config.status === 'TESTED' && (isTenantAdmin || isPlatformAdmin) && (
              <button
                onClick={handleApprove}
                disabled={isLoading}
                className="admin-btn admin-btn--secondary"
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  color: '#6EE7B7',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '8px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Approve Config
              </button>
            )}

            <button
              onClick={handleSaveDraft}
              disabled={isLoading || config.status === 'PUBLISHED'}
              className="admin-btn admin-btn--secondary"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Save Draft {hasUnsavedChanges && '●'}
            </button>

            {(isTenantAdmin || isPlatformAdmin) && (
              <button
                onClick={handlePublish}
                disabled={isLoading}
                className="admin-btn admin-btn--primary"
                style={{
                  background: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: 700,
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                }}
              >
                Publish to Production
              </button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            padding: '60px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            minHeight: '380px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
          }}
        >
          <div
            className="dashboard-spinner"
            style={{ width: '40px', height: '40px', borderWidth: '3px', borderColor: '#2563eb', borderTopColor: 'transparent' }}
          />
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
              Loading AI Evaluation Studio...
            </h4>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)' }}>
              Retrieving scenario evaluation rubrics, scoring anchors, prompt templates, and versions.
            </p>
          </div>
        </div>
      ) : (
        <>

      {/* Case Selector Banner */}
      <div
        className="eval-case-selector-banner"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'var(--surface)',
          padding: '12px 18px',
          borderRadius: '10px',
          border: '1px solid var(--line)',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div className="eval-case-selector-left" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🎯</span> Active Scenario Case:
          </label>
          <select
            value={selectedCaseId}
            onChange={(e) => handleCaseChange(e.target.value)}
            className="admin-form__select eval-case-select"
            style={{ width: 'auto', minWidth: '220px', maxWidth: '100%', padding: '6px 12px', height: '38px', fontWeight: 600, fontSize: '13px' }}
          >
            {selectedCaseId && !cases.some((c) => c.id === selectedCaseId) && (
              <option value={selectedCaseId}>
                {selectedCaseId.replace(/_/g, ' ').toUpperCase()} (Active Scenario)
              </option>
            )}
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} {c.evaluationStatus === 'PUBLISHED' ? `(v${c.publishedVersion} Published)` : `(${c.evaluationStatus})`}
              </option>
            ))}
          </select>
          {selectedCaseId && (
            <span className="eval-case-id-badge" style={{ fontSize: '11px', color: 'var(--muted)', background: 'var(--white)', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--line)' }}>
              ID: <code>{selectedCaseId}</code>
            </span>
          )}
        </div>

        {allVersions.length > 0 && (
          <div className="eval-case-selector-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 500 }}>Config Version:</span>
            <select
              value={config.id || ''}
              onChange={(e) => {
                const target = allVersions.find((v) => v.id === e.target.value);
                if (target) {
                  setConfig(target);
                  setInitialConfig(target);
                }
              }}
              className="admin-form__select eval-version-select"
              style={{ width: 'auto', padding: '4px 10px', height: '34px', fontSize: '12px', fontWeight: 500 }}
            >
              {allVersions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version} — {v.status} ({new Date(v.updatedAt || '').toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Alerts */}
      {statusMessage && statusMessage.type === 'error' && (
        <div
          className="admin-alert admin-alert--error"
          style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{statusMessage.text}</span>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'inherit', padding: '0 4px' }}
          >
            ✕
          </button>
        </div>
      )}

      {concurrencyConflict && (
        <div className="admin-alert admin-alert--error" style={{ marginBottom: '16px' }}>
          <strong>Conflict Detected:</strong> Another administrator saved a modification to this configuration. Please refresh to load the newest version.
          <button
            onClick={() => handleCaseChange(selectedCaseId)}
            className="admin-btn admin-btn--sm admin-btn--secondary"
            style={{ marginLeft: '12px' }}
          >
            Reload Version
          </button>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="admin-alert admin-alert--error" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <strong>Configuration Errors:</strong>
            {validationErrors.some((e) => {
              const lower = (e || '').toLowerCase();
              return lower.includes('variable') || lower.includes('prompt') || lower.includes('template') || lower.includes('unknown');
            }) && (
              <button
                type="button"
                onClick={handleAutoFixPrompts}
                className="admin-btn admin-btn--sm admin-btn--primary"
                style={{ background: '#2563EB', color: '#FFF', display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                ⚡ Auto-Fix Prompts & Variables
              </button>
            )}
          </div>
          <ul style={{ margin: '6px 0 0 16px' }}>
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Navigation Tabs */}
      <div
        className="tabs eval-studio-tabs"
        role="tablist"
        style={{
          display: 'flex',
          gap: '6px',
          borderBottom: '2px solid var(--line)',
          paddingBottom: '2px',
          marginBottom: '20px',
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {[
          { key: 'general', label: 'General', count: undefined },
          { key: 'competencies', label: 'Competencies', count: config.competencies.length },
          { key: 'criteria', label: 'Rubric Criteria', count: config.criteria.length },
          { key: 'scoring', label: 'Scoring Scale', count: undefined },
          { key: 'evidence', label: 'Evidence Sources', count: undefined },
          { key: 'prompts', label: 'Prompt Studio', count: undefined },
          { key: 'llm', label: 'LLM & Provider', count: undefined },
          { key: 'preview', label: 'Prompt Preview', count: undefined },
          { key: 'test', label: 'Test Evaluation', count: undefined },
          { key: 'golden', label: 'Golden Calibration', count: undefined },
          { key: 'diff', label: 'Version Diff', count: undefined },
          { key: 'audit', label: 'Audit Trail', count: auditLogs.length },
        ].map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              className={`tab ${isActive ? 'tab--active active' : ''}`}
              style={{
                whiteSpace: 'nowrap',
                padding: '8px 14px',
                borderRadius: '8px 8px 0 0',
                fontWeight: isActive ? 700 : 500,
                fontSize: '13px',
                color: isActive ? '#2563EB' : 'var(--ink)',
                borderBottom: isActive ? '2px solid #2563EB' : '2px solid transparent',
                background: isActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s ease',
              }}
              onClick={() => setActiveTab(tab.key as any)}
            >
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '1px 6px',
                    borderRadius: '10px',
                    background: isActive ? '#2563EB' : 'var(--line)',
                    color: isActive ? '#FFFFFF' : 'var(--muted)',
                    fontWeight: 600,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Panels */}
      <div
        className="admin-section eval-studio-section"
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div style={{ display: 'grid', gap: '16px', maxWidth: '800px' }}>
            <div className="admin-form__group">
              <label className="admin-form__label">Configuration Name</label>
              <input
                type="text"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Description</label>
              <textarea
                rows={3}
                value={config.description}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                className="admin-form__textarea"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label className="admin-form__label">Status</label>
                <input type="text" readOnly value={config.status} className="admin-form__input" style={{ background: 'var(--surface)' }} />
              </div>
              <div>
                <label className="admin-form__label">Version</label>
                <input type="text" readOnly value={`v${config.version}`} className="admin-form__input" style={{ background: 'var(--surface)' }} />
              </div>
            </div>
          </div>
        )}

        {/* COMPETENCIES TAB */}
        {activeTab === 'competencies' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '15px', margin: 0 }}>Case Competency Dimensions</h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                  Define high-level evaluation competencies and normalized weights (Total: {(totalCompetencyWeight * 100).toFixed(0)}%).
                </p>
              </div>
              <button
                onClick={() =>
                  setConfig({
                    ...config,
                    competencies: [
                      ...config.competencies,
                      { id: `comp_${config.competencies.length + 1}`, name: 'New Competency', weight: 0.25, description: '' },
                    ],
                  })
                }
                className="admin-btn admin-btn--secondary admin-btn--sm"
              >
                + Add Competency
              </button>
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
              {config.competencies.map((comp, idx) => (
                <div
                  key={idx}
                  className="eval-competency-card"
                  style={{
                    background: 'var(--surface)',
                    padding: '14px',
                    borderRadius: '8px',
                    border: '1px solid var(--line)',
                    display: 'grid',
                    gap: '10px',
                  }}
                >
                  <div className="eval-competency-row">
                    <input
                      type="text"
                      placeholder="ID (e.g. discovery)"
                      value={comp.id}
                      onChange={(e) => {
                        const updated = [...config.competencies];
                        updated[idx].id = e.target.value;
                        setConfig({ ...config, competencies: updated });
                      }}
                      className="admin-form__input eval-comp-id"
                    />
                    <input
                      type="text"
                      placeholder="Display Name"
                      value={comp.name}
                      onChange={(e) => {
                        const updated = [...config.competencies];
                        updated[idx].name = e.target.value;
                        setConfig({ ...config, competencies: updated });
                      }}
                      className="admin-form__input eval-comp-name"
                    />
                    <div className="eval-comp-weight">
                      <span style={{ fontSize: '12px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Weight:</span>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        max="1"
                        value={comp.weight}
                        onChange={(e) => {
                          const updated = [...config.competencies];
                          updated[idx].weight = Number(e.target.value);
                          setConfig({ ...config, competencies: updated });
                        }}
                        className="admin-form__input eval-comp-weight-inp"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = config.competencies.filter((_, i) => i !== idx);
                        setConfig({ ...config, competencies: updated });
                      }}
                      className="admin-btn admin-btn--danger admin-btn--sm eval-comp-del"
                      title="Remove Competency"
                    >
                      ×
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Competency description & assessment intent..."
                    value={comp.description}
                    onChange={(e) => {
                      const updated = [...config.competencies];
                      updated[idx].description = e.target.value;
                      setConfig({ ...config, competencies: updated });
                    }}
                    className="admin-form__input eval-comp-desc"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RUBRIC CRITERIA TAB */}
        {activeTab === 'criteria' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '15px', margin: 0 }}>Rubric Criteria & Behavioral Anchors</h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                  Configure granular criteria with critical failure flags and passing/failing behavioral anchors (Total Weight: {(totalCriteriaWeight * 100).toFixed(0)}%).
                </p>
              </div>
              <button
                onClick={() =>
                  setConfig({
                    ...config,
                    criteria: [
                      ...config.criteria,
                      {
                        id: `crit_${config.criteria.length + 1}`,
                        name: 'New Criterion',
                        competencyId: config.competencies[0]?.id || '',
                        weight: 0.25,
                        criticalFlag: false,
                        description: '',
                        passingAnchor: '',
                        failingAnchor: '',
                      },
                    ],
                  })
                }
                className="admin-btn admin-btn--secondary admin-btn--sm"
              >
                + Add Criterion
              </button>
            </div>

            <div style={{ display: 'grid', gap: '14px' }}>
              {config.criteria.map((crit, idx) => (
                <div
                  key={idx}
                  className="eval-criteria-card"
                  style={{
                    background: 'var(--surface)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid var(--line)',
                    display: 'grid',
                    gap: '10px',
                  }}
                >
                  <div className="eval-criteria-row">
                    <input
                      type="text"
                      placeholder="Criterion ID"
                      value={crit.id}
                      onChange={(e) => {
                        const updated = [...config.criteria];
                        updated[idx].id = e.target.value;
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-form__input eval-crit-id"
                    />
                    <input
                      type="text"
                      placeholder="Criterion Name"
                      value={crit.name}
                      onChange={(e) => {
                        const updated = [...config.criteria];
                        updated[idx].name = e.target.value;
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-form__input eval-crit-name"
                    />
                    <select
                      value={crit.competencyId || ''}
                      onChange={(e) => {
                        const updated = [...config.criteria];
                        updated[idx].competencyId = e.target.value;
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-form__select eval-crit-comp"
                    >
                      <option value="">(Select Competency)</option>
                      {config.competencies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <label className="eval-crit-flag" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--bad)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={crit.criticalFlag}
                        onChange={(e) => {
                          const updated = [...config.criteria];
                          updated[idx].criticalFlag = e.target.checked;
                          setConfig({ ...config, criteria: updated });
                        }}
                      />
                      <span>Critical Failure</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = config.criteria.filter((_, i) => i !== idx);
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-btn admin-btn--danger admin-btn--sm eval-crit-del"
                      title="Remove Criterion"
                    >
                      ×
                    </button>
                  </div>

                  <input
                    type="text"
                    placeholder="Criterion description..."
                    value={crit.description}
                    onChange={(e) => {
                      const updated = [...config.criteria];
                      updated[idx].description = e.target.value;
                      setConfig({ ...config, criteria: updated });
                    }}
                    className="admin-form__input eval-crit-desc"
                  />

                  <div className="eval-behavioral-anchors-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <textarea
                      rows={2}
                      placeholder="Passing behavioral anchor (What good looks like)..."
                      value={crit.passingAnchor || ''}
                      onChange={(e) => {
                        const updated = [...config.criteria];
                        updated[idx].passingAnchor = e.target.value;
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-form__textarea eval-anchor-textarea"
                      style={{ fontSize: '12px' }}
                    />
                    <textarea
                      rows={2}
                      placeholder="Failing behavioral anchor (What poor looks like)..."
                      value={crit.failingAnchor || ''}
                      onChange={(e) => {
                        const updated = [...config.criteria];
                        updated[idx].failingAnchor = e.target.value;
                        setConfig({ ...config, criteria: updated });
                      }}
                      className="admin-form__textarea eval-anchor-textarea"
                      style={{ fontSize: '12px' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SCORING SCALE TAB */}
        {activeTab === 'scoring' && (
          <div style={{ display: 'grid', gap: '18px', maxWidth: '700px' }}>
            <h3 style={{ fontSize: '15px', margin: 0 }}>Scoring Scale & Thresholds</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div className="admin-form__group">
                <label className="admin-form__label">Min Score</label>
                <input
                  type="number"
                  value={config.scoringScale.min}
                  onChange={(e) => setConfig({ ...config, scoringScale: { ...config.scoringScale, min: Number(e.target.value) } })}
                  className="admin-form__input"
                />
              </div>
              <div className="admin-form__group">
                <label className="admin-form__label">Max Score</label>
                <input
                  type="number"
                  value={config.scoringScale.max}
                  onChange={(e) => setConfig({ ...config, scoringScale: { ...config.scoringScale, max: Number(e.target.value) } })}
                  className="admin-form__input"
                />
              </div>
              <div className="admin-form__group">
                <label className="admin-form__label">Pass Mark Threshold (%)</label>
                <input
                  type="number"
                  value={config.scoringScale.passMark}
                  onChange={(e) => setConfig({ ...config, scoringScale: { ...config.scoringScale, passMark: Number(e.target.value) } })}
                  className="admin-form__input"
                />
              </div>
            </div>

            <div className="admin-form__group">
              <label className="admin-form__label">Critical Criterion Failure Action</label>
              <select
                value={config.scoringScale.criticalFailureAction || 'CAP_AT_FAILING'}
                onChange={(e) => setConfig({ ...config, scoringScale: { ...config.scoringScale, criticalFailureAction: e.target.value } })}
                className="admin-form__select"
              >
                <option value="CAP_AT_FAILING">Cap overall score below passMark (Strict PRD Standard)</option>
                <option value="FAIL_ATTEMPT">Directly mark attempt status as FAILED</option>
                <option value="WARNING_ONLY">Issue prominent critical warning without score cap</option>
              </select>
            </div>
          </div>
        )}

        {/* EVIDENCE SOURCES TAB */}
        {activeTab === 'evidence' && (
          <div style={{ display: 'grid', gap: '16px', maxWidth: '700px' }}>
            <h3 style={{ fontSize: '15px', margin: 0 }}>Unified Multimodal Evidence Inputs</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
              Control which evidence streams from Phase 25 aggregator are provided to the evaluation LLM context.
            </p>

            <div style={{ display: 'grid', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.evidenceSources.transcript}
                  onChange={(e) => setConfig({ ...config, evidenceSources: { ...config.evidenceSources, transcript: e.target.checked } })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--blue)' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>Verbatim Transcript (Required)</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Chronological spoken utterances with timestamps and speaker tags</div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.evidenceSources.communicationMetrics}
                  onChange={(e) => setConfig({ ...config, evidenceSources: { ...config.evidenceSources, communicationMetrics: e.target.checked } })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--blue)' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>Communication Metrics</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Calculated speech rate (WPM), turn count, latency, and pause spans</div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.evidenceSources.videoEvidence}
                  onChange={(e) => setConfig({ ...config, evidenceSources: { ...config.evidenceSources, videoEvidence: e.target.checked } })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--blue)' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>Video Observational Signals</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Observable face visibility, head movement spans, looking away events (Zero raw video)</div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--line)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.evidenceSources.nlpEvidence}
                  onChange={(e) => setConfig({ ...config, evidenceSources: { ...config.evidenceSources, nlpEvidence: e.target.checked } })}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--blue)' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)' }}>NLP Topic & Entity Coverage (Optional)</div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Extracted key facts, covered requirements, and topic transitions</div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* PROMPT STUDIO TAB */}
        {activeTab === 'prompts' && (
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Action Bar & Quick Palette */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                padding: '12px 16px',
                background: 'var(--surface)',
                borderRadius: '8px',
                border: '1px solid var(--line)',
              }}
            >
              <div>
                <strong style={{ fontSize: '13px', color: 'var(--ink)' }}>Prompt Assistant & Variables</strong>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
                  Click any allowed tag to insert or copy into your prompts. Use Auto-Fix to automatically resolve aliases and bracket errors.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleAutoFixPrompts}
                  className="admin-btn admin-btn--sm admin-btn--primary"
                  style={{ background: '#2563EB', color: '#FFF', display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  ⚡ Auto-Fix All Prompts
                </button>
                <button
                  type="button"
                  onClick={handleResetToGoldenPrompts}
                  className="admin-btn admin-btn--sm admin-btn--secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  ✨ Reset to Golden Blueprint
                </button>
              </div>
            </div>

            {/* Allowed Variables Palette */}
            <div style={{ background: 'rgba(37, 99, 235, 0.04)', padding: '14px 16px', borderRadius: '8px', border: '1px solid rgba(37, 99, 235, 0.15)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--blue)' }}>
                  Allowed Prompt Template Variables (Phase 14 & 27):
                </span>
                {copiedVar && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#15803D',
                      fontWeight: 600,
                      background: '#DCFCE7',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      border: '1px solid #86EFAC',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      animation: 'fadeIn 0.2s ease-in-out',
                    }}
                  >
                    ✓ Copied <code>{`{{${copiedVar}}}`}</code> to clipboard
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {ALLOWED_PROMPT_VARS.map((v) => {
                  const isCopied = copiedVar === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(`{{${v}}}`);
                        setCopiedVar(v);
                        setTimeout(() => {
                          setCopiedVar((curr) => (curr === v ? null : curr));
                        }, 2000);
                      }}
                      title={`Click to copy {{${v}}}`}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: isCopied ? 700 : 500,
                        background: isCopied ? '#DCFCE7' : '#FFFFFF',
                        border: isCopied ? '1px solid #16A34A' : '1px solid var(--line)',
                        color: isCopied ? '#15803D' : 'var(--blue)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.15s ease-in-out',
                        transform: isCopied ? 'scale(1.03)' : 'scale(1)',
                        boxShadow: isCopied ? '0 2px 6px rgba(22, 163, 74, 0.2)' : 'none',
                      }}
                    >
                      {isCopied ? '✓' : '+'} {`{{${v}}}`}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Evaluation Prompt */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="admin-form__label" style={{ fontSize: '14px', margin: 0, fontWeight: 600 }}>
                  Evaluation Prompt Template
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const fixed = autoFixPromptTemplate(config.evaluationPrompt, 'evaluation');
                      setConfig({ ...config, evaluationPrompt: fixed });
                      setStatusMessage({ type: 'success', text: 'Evaluation prompt auto-fixed.' });
                    }}
                    className="admin-btn admin-btn--sm admin-btn--secondary"
                    style={{ fontSize: '11px' }}
                  >
                    ⚡ Auto-Fix Template
                  </button>
                  <button onClick={() => handlePreviewPrompt('evaluation')} className="admin-btn admin-btn--sm admin-btn--secondary">
                    Preview Evaluation Prompt →
                  </button>
                </div>
              </div>
              <textarea
                rows={7}
                value={config.evaluationPrompt}
                onChange={(e) => setConfig({ ...config, evaluationPrompt: e.target.value })}
                className="admin-form__textarea"
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>

            {/* Coaching Prompt */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="admin-form__label" style={{ fontSize: '14px', margin: 0, fontWeight: 600 }}>
                  Coaching Prompt Template
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const fixed = autoFixPromptTemplate(config.coachingPrompt, 'coaching');
                      setConfig({ ...config, coachingPrompt: fixed });
                      setStatusMessage({ type: 'success', text: 'Coaching prompt auto-fixed.' });
                    }}
                    className="admin-btn admin-btn--sm admin-btn--secondary"
                    style={{ fontSize: '11px' }}
                  >
                    ⚡ Auto-Fix Template
                  </button>
                  <button onClick={() => handlePreviewPrompt('coaching')} className="admin-btn admin-btn--sm admin-btn--secondary">
                    Preview Coaching Prompt →
                  </button>
                </div>
              </div>
              <textarea
                rows={5}
                value={config.coachingPrompt}
                onChange={(e) => setConfig({ ...config, coachingPrompt: e.target.value })}
                className="admin-form__textarea"
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>

            {/* Result Summary Prompt */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="admin-form__label" style={{ fontSize: '14px', margin: 0, fontWeight: 600 }}>
                  Result Summary Prompt Template
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const fixed = autoFixPromptTemplate(config.resultPrompt, 'result');
                      setConfig({ ...config, resultPrompt: fixed });
                      setStatusMessage({ type: 'success', text: 'Result prompt auto-fixed.' });
                    }}
                    className="admin-btn admin-btn--sm admin-btn--secondary"
                    style={{ fontSize: '11px' }}
                  >
                    ⚡ Auto-Fix Template
                  </button>
                  <button onClick={() => handlePreviewPrompt('result')} className="admin-btn admin-btn--sm admin-btn--secondary">
                    Preview Result Prompt →
                  </button>
                </div>
              </div>
              <textarea
                rows={4}
                value={config.resultPrompt}
                onChange={(e) => setConfig({ ...config, resultPrompt: e.target.value })}
                className="admin-form__textarea"
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>
          </div>
        )}

        {/* LLM & PROVIDER TAB */}
        {activeTab === 'llm' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            <div className="admin-form__group">
              <label className="admin-form__label">LLM Provider</label>
              <select
                value={config.llmProvider}
                onChange={(e) => setConfig({ ...config, llmProvider: e.target.value })}
                className="admin-form__select"
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic Claude</option>
                <option value="gemini">Google Gemini</option>
                <option value="ollama">Ollama (Local)</option>
                <option value="mock">Mock / Test Baseline</option>
              </select>
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Evaluation Model</label>
              <input
                type="text"
                value={config.llmModel}
                onChange={(e) => setConfig({ ...config, llmModel: e.target.value })}
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Temperature ({config.temperature})</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={config.temperature}
                onChange={(e) => setConfig({ ...config, temperature: Number(e.target.value) })}
                style={{ height: '40px', accentColor: 'var(--blue)' }}
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Max Tokens</label>
              <input
                type="number"
                value={config.maxTokens}
                onChange={(e) => setConfig({ ...config, maxTokens: Number(e.target.value) })}
                className="admin-form__input"
              />
            </div>
          </div>
        )}

        {/* PROMPT PREVIEW TAB */}
        {activeTab === 'preview' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '15px', margin: 0 }}>Rendered Template Preview ({previewingPromptType})</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {(['evaluation', 'coaching', 'result'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => handlePreviewPrompt(t)}
                    className={`admin-btn admin-btn--sm ${previewingPromptType === t ? 'admin-btn--primary' : 'admin-btn--secondary'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {promptPreview ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--muted)', background: 'var(--surface)', padding: '10px 14px', borderRadius: '8px' }}>
                  <span>Variables Interpolated: <strong>{promptPreview.variablesUsed.join(', ') || 'None'}</strong></span>
                  <span>Estimated Context Tokens: <strong>~{promptPreview.estimatedTokens}</strong></span>
                  <span>Est. Cost: <strong>${promptPreview.estimatedCost.toFixed(5)}</strong></span>
                </div>
                <pre style={{ background: 'var(--surface)', padding: '16px', borderRadius: '8px', border: '1px solid var(--line)', whiteSpace: 'pre-wrap', fontSize: '12px', fontFamily: 'monospace' }}>
                  {promptPreview.rendered}
                </pre>
              </div>
            ) : (
              <p style={{ color: 'var(--muted)' }}>Click a preview button above to render prompt template with safe benchmark data.</p>
            )}
          </div>
        )}

        {/* TEST EVALUATION TAB */}
        {activeTab === 'test' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '15px', margin: 0 }}>Isolated Test Evaluation</h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                  Executes draft evaluation prompt without altering learner results, consumption quotas, or production analytics.
                </p>
              </div>
              <button onClick={handleRunTest} disabled={isTesting} className="admin-btn admin-btn--primary">
                {isTesting ? 'Running Test...' : 'Run Test Evaluation'}
              </button>
            </div>

            {testResult && (
              <div style={{ background: 'var(--surface)', padding: '16px', borderRadius: '8px', border: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '13px' }}>
                  <span>Latency: <strong>{testResult.latencyMs || 0}ms</strong></span>
                  <span>Model: <strong>{testResult.model || config.llmModel}</strong></span>
                  <span>Tokens: <strong>{testResult.tokensUsed?.totalTokens || 0}</strong></span>
                  <span>Schema: <strong style={{ color: testResult.schemaValidation?.valid ? 'var(--ok)' : 'var(--bad)' }}>{testResult.schemaValidation?.valid ? 'VALID' : 'INVALID'}</strong></span>
                </div>
                <pre style={{ background: 'var(--white)', padding: '12px', borderRadius: '6px', border: '1px solid var(--line)', fontSize: '12px', overflowX: 'auto' }}>
                  {JSON.stringify(testResult.rawResponse || testResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* GOLDEN CALIBRATION TAB */}
        {activeTab === 'golden' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '15px', margin: 0 }}>Golden Evaluation Set Calibration (Phase 26)</h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
                  Run automated quality benchmarks against calibrated golden attempts to verify score stability and evidence grounding.
                </p>
              </div>
              <button onClick={handleRunGoldenSet} disabled={isGoldenRunning} className="admin-btn admin-btn--primary">
                {isGoldenRunning ? 'Calibrating...' : 'Run Golden Calibration'}
              </button>
            </div>

            {goldenResult && (
              <div style={{ background: 'var(--surface)', padding: '16px', borderRadius: '8px', border: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
                  <span>Total Attempts: <strong>{goldenResult.totalGoldenAttempts || 0}</strong></span>
                  <span>Passed: <strong style={{ color: 'var(--ok)' }}>{goldenResult.passedCount || 0}</strong></span>
                  <span>Failed: <strong style={{ color: goldenResult.failedCount ? 'var(--bad)' : 'var(--ok)' }}>{goldenResult.failedCount || 0}</strong></span>
                </div>
                {goldenResult.results && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {goldenResult.results.map((r: any, i: number) => (
                      <div key={i} style={{ background: 'var(--white)', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{r.name || `Golden Attempt ${i + 1}`}</span>
                        <span style={{ color: r.passed ? 'var(--ok)' : 'var(--bad)', fontWeight: 600 }}>{r.passed ? 'PASSED' : 'FAILED'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* VERSION DIFF TAB */}
        {activeTab === 'diff' && (
          <div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '13px', fontWeight: 600 }}>Compare with version:</label>
              <select
                value={selectedDiffVersionId}
                onChange={(e) => setSelectedDiffVersionId(e.target.value)}
                className="admin-form__select"
                style={{ width: 'auto', minWidth: '220px' }}
              >
                <option value="">(Select Version)</option>
                {allVersions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version} ({v.status})
                  </option>
                ))}
              </select>
              <button onClick={handleRunDiff} className="admin-btn admin-btn--secondary admin-btn--sm">
                Compute Diff
              </button>
            </div>

            {diffResult && (
              <div style={{ background: 'var(--surface)', padding: '16px', borderRadius: '8px', border: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                  <span>Score Delta: <strong>{diffResult.scoreDiff > 0 ? `+${diffResult.scoreDiff}` : diffResult.scoreDiff}</strong></span>
                </div>
                {diffResult.competencyDiffs?.length > 0 && (
                  <div className="admin-table-container" style={{ marginTop: '8px' }}>
                    <table className="admin-table" style={{ width: '100%', background: 'var(--white)', borderRadius: '6px' }}>
                      <thead>
                        <tr>
                          <th>Competency</th>
                          <th>Baseline Score</th>
                          <th>Draft Score</th>
                          <th>Delta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffResult.competencyDiffs.map((cd: any, idx: number) => (
                          <tr key={idx}>
                            <td>{cd.name}</td>
                            <td>{cd.scoreA}</td>
                            <td>{cd.scoreB}</td>
                            <td style={{ color: cd.diff > 0 ? 'var(--ok)' : cd.diff < 0 ? 'var(--bad)' : 'var(--ink)', fontWeight: 600 }}>
                              {cd.diff > 0 ? `+${cd.diff}` : cd.diff}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* AUDIT TRAIL TAB */}
        {activeTab === 'audit' && (
          <div>
            <h3 style={{ fontSize: '15px', marginBottom: '14px' }}>Governance & Calibration Audit Trail</h3>
            {auditLogs.length === 0 ? (
              <p style={{ color: 'var(--muted)', fontSize: '13px' }}>No audit actions recorded for this scenario yet.</p>
            ) : (
              <div className="admin-table-container">
                <table className="admin-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Action</th>
                      <th>Version</th>
                      <th>Actor</th>
                      <th>Action Controls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, idx) => (
                      <tr key={idx}>
                        <td style={{ fontSize: '12.5px', color: 'var(--muted)' }}>{new Date(log.timestamp).toLocaleString()}</td>
                        <td>
                          <span className="admin-status-badge admin-status-badge--active" style={{ fontSize: '11px' }}>
                            {log.action}
                          </span>
                        </td>
                        <td>v{log.version}</td>
                        <td>{log.actor || 'system'}</td>
                        <td>
                          {log.action === 'APPROVE' && (isTenantAdmin || isPlatformAdmin) && (
                            <button
                              onClick={() => handleRollback(log.configurationId)}
                              className="admin-btn admin-btn--sm admin-btn--secondary"
                              style={{ padding: '2px 8px', fontSize: '11px' }}
                            >
                              Rollback to v{log.version}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Non-Intrusive Notification Toast */}
      {statusMessage && statusMessage.type !== 'error' && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 18px',
            borderRadius: '10px',
            background: statusMessage.type === 'success' ? '#065F46' : '#1E40AF',
            color: '#FFFFFF',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
            fontSize: '13px',
            fontWeight: 500,
            border: statusMessage.type === 'success' ? '1px solid #10B981' : '1px solid #3B82F6',
            animation: 'fadeIn 0.2s ease-in-out',
            maxWidth: '420px',
          }}
        >
          <span>{statusMessage.type === 'success' ? '✓' : 'ℹ'}</span>
          <span style={{ flex: 1 }}>{statusMessage.text}</span>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#FFFFFF',
              opacity: 0.8,
              cursor: 'pointer',
              fontSize: '14px',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};
